import React, { Component, ReactNode } from "react";

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Rendered when a child throws during render/commit. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Called when an error is captured (logging, telemetry). */
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** Re-mount children when this value changes (e.g. a src/key). */
  resetKey?: unknown;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Containment boundary that prevents a crashing subtree (e.g. a third-party
 * DOM-mutating component like Plyr) from blanking the whole React tree.
 *
 * Without this, an uncaught render/commit error unmounts the entire app and
 * the user sees a white screen. The boundary isolates the failure to the
 * affected component and renders a fallback instead.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      return null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
